// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import clsx from "clsx";
import moment from "moment";
import { ColumnsType } from "antd/es/table";
import { Link, useNavigate } from "react-router-dom";
import { ReactSVG } from "react-svg";
import { useSelector } from "react-redux";

// Local Imports
import CustomTable from "../../../../components/custom-table/custom-table";
import CustomSearch from "../../../../components/custom-search/custom-search";
import OnlineTradingService from "../../online-trading.service";
import { Switch } from "antd";

const OnlineTradingTable: React.FC = (): ReactElement => {
  let searchTimer = null;
  const onlineTradingService = new OnlineTradingService();
  const loadingState = useSelector((state: any) => state?.loading);
  const [onlineTradingList, setOnlineTradingList] = useState<any[]>([]);
  const [responseDate, setResponseDate] = useState<any>({});
  const [searchWord, setSearchWord] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    fetchOnlineTradingListData();
  }, []);

  const handleTradingAccessChange = async (portfolioId: number, isTradingAllowed: boolean): Promise<void> => {
    const data = await onlineTradingService.toggleTradingAccess(portfolioId, isTradingAllowed);
    if (data) {
      fetchOnlineTradingListData();
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      render: (_, __, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Client account",
      dataIndex: "accountCode",
      key: "accountCode",
      sorter: true,
      fixed: "left",
      render: (_, record) => (
        <Link
          to={`/home/business-account/${record?.accountId}/${record?.account?.activeKycId}/details`}
        >
          {record?.account?.accountCode}
        </Link>
      ),
    },
    {
      title: "Client name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (_, record) => record?.account?.activeKyc?.nameEn,
    },
    {
      title: "Portfolio number",
      dataIndex: "accountCode",
      key: "accountCode",
      sorter: true,
    },
    {
      title: "Portfolio type",
      dataIndex: "portfolioType",
      key: "portfolioType",
      render: (_, record) => <span>{record?.portfolioType?.nameEn}</span>,
    },
    {
      title: "Portfolio Type",
      dataIndex: "portfolioType",
      key: "portfolioType",
      render: (_, record) => <span>{record?.portfolioType?.nameEn}</span>,
    },
    {
      title: "Trading access",
      dataIndex: "isTradingAllowed",
      key: "isTradingAllowed",
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <Switch
            checked={record?.isTradingAllowed}
            onChange={(checked) => {
              handleTradingAccessChange(record?.id, checked);
            }}
          />
        </div>
      ),
    },
    {
      title: "Market",
      dataIndex: "exchange",
      key: "exchange",
      render: (text) => <span>{text?.join(", ") || "-"}</span>,
    },
    {
      title: "Updated By",
      dataIndex: "tradingCapUpdatedBy",
      key: "tradingCapUpdatedBy",
      render: (_, record) => <span>{record?.tradingCapUpdatedBy?.name || "-"}</span>,
    },
    {
      title: "Updated At",
      dataIndex: "tradingCapUpdatedAt",
      key: "tradingCapUpdatedAt",
      render: (_, record) => <span>{record?.tradingCapUpdatedAt ? moment(record?.tradingCapUpdatedAt).format("MMM DD YYYY hh:mm A") : "-"}</span>,
    },

  ];

  const fetchOnlineTradingListData = async (page = 1): Promise<void> => {
    const data = await onlineTradingService.getOnlineTradingListData(
      ["Opened"],
      true,
      page,
      searchWord,
      sortField,
      sortOrder,
      filters
    );

    if (data !== undefined) {
      setResponseDate(data.data);
      setOnlineTradingList(data.data?.portfolios);
    }
  };

  useEffect(() => {
    fetchOnlineTradingListData();
  }, [searchWord, sortField, sortOrder, filters]);

  const handleTableChange = (pagination, filters, sorter) => {
    setFilters(JSON.stringify(filters?.status));
    setSortField(sorter.field);
    setSortOrder(sorter.order);
  };

  const onSearch = (e) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setSearchWord(e.target.value);
    }, 1000);
  };

  return (
    <div className=" mt-5 border-t-[1px] border-gray-400 rounded-lg">
      <div className="flex items-center justify-between rounded-lg bg-white py-[10px] px-5">
        <p className=" text-black-300 text-base font-bold">
          {!loadingState ? responseDate?.totalCount : ""} portfolios
        </p>
        <div className="flex items-center">
          <CustomSearch onChange={(event) => onSearch(event)} />
        </div>
      </div>
      {onlineTradingList?.length ? (
        <CustomTable
          columns={columns}
          data={onlineTradingList}
          showPagination={true}
          pageSize={responseDate?.pageSize}
          totalCount={responseDate?.totalCount}
          page={responseDate?.currentPage}
          handleTableChange={handleTableChange}
          onPageChange={(page) => {
            fetchOnlineTradingListData(page);
          }}
        />
      ) : !loadingState ? (
        <div className="flex flex-col gap-3 w-full items-center justify-center mt-9">
          <img
            className="w-[40px] h-[40px]"
            src="/images/no-items.svg"
            alt="no data kamco"
          />
          <p className="text-lg text-gray-500 font-medium">
            There are no items in this list
          </p>
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default OnlineTradingTable;
